import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import OtpModal from "./otpModal";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
const BACKEND_URL = "https://supizza-backend.onrender.com";

export default function Checkout() {
  const location = useLocation();

  // ---------------- STATES ----------------
  const [cartItems, setCartItems] = useState(() => {
    const buyNowItem = JSON.parse(sessionStorage.getItem("buyNowItem"));
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    if (buyNowItem) return [buyNowItem];
    if (location.state?.fromCart) return storedCart;
    return [];
  });

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.unitPrice * item.quantity,
    0,
  );
  const tax = Math.round(subtotal * 0.16);
  const delivery = cartItems.length > 0 && subtotal < 3000 ? 200 : 0;
  const grandTotal = subtotal + tax + delivery;
  const totalItems = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const [step, setStep] = useState("loading"); // email | info | payment
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
  });
  const [modalForm, setModalForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [processingOrder, setProcessingOrder] = useState(false);
  const [progress, setProgress] = useState(0);
  const [orderCancelled, setOrderCancelled] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState("");
  const [showCancel, setShowCancel] = useState(true);
  const [isOrderSubmitting, setIsOrderSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const orderTimeoutRef = useRef(null);

  // ----------mask emial--------------
  const maskEmail = (email) => {
    const [user, domain] = email.split("@");
    if (user.length <= 3) return `***@${domain}`;
    const visible = user.slice(0, 3);
    return `${visible}***@${domain}`;
  };
  // -----------mask mobile number------------------------
  const maskMobile = (mobile) => {
    if (mobile.length !== 11) return mobile; // only valid PK numbers
    return `${mobile.slice(0, 4)}****${mobile.slice(8)}`;
  };
  // ---------------- FETCH USER ----------------
  const fetchUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setStep("email");
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 404) {
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        setUser(null);
        setStep("email");
        window.dispatchEvent(new Event("userLoggedOut"));
        return;
      }

      const data = await res.json();
      setUser(data.user);
      setFormData(data.user);
      setModalForm(data.user);

      if (
        data.user.name &&
        data.user.phone &&
        data.user.city &&
        data.user.address
      ) {
        setStep("payment");
      } else {
        setStep("info");
        setEditModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      setUser(null);
      setStep("email");
    }
  };

  useEffect(() => {
    fetchUser();

    const handleUserUpdated = () => fetchUser();
    const handleUserLoggedOut = () => {
      setUser(null);
      setFormData({ name: "", phone: "", email: "", city: "", address: "" });
      setStep("email");
      setOtpModalOpen(false);
    };

    window.addEventListener("userUpdated", handleUserUpdated);
    window.addEventListener("userLoggedOut", handleUserLoggedOut);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdated);
      window.removeEventListener("userLoggedOut", handleUserLoggedOut);
    };
  }, []);

  // ---------------- INPUT HANDLERS ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setModalForm((prev) => ({ ...prev, [name]: value }));

    if (name === "phone")
      setPhoneError(
        /^03\d{9}$/.test(value)
          ? ""
          : "Mobile must start with 03 and be 11 digits",
      );
    if (name === "email")
      setEmailError(
        /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(value) ? "" : "Invalid email",
      );
  };

  const isSaveDisabled = () => {
    if (!modalForm.name.trim() || !modalForm.address.trim()) return true;
    if (!/^03\d{9}$/.test(modalForm.phone)) return true;
    if (
      user &&
      modalForm.name === user.name &&
      modalForm.phone === user.phone &&
      modalForm.city === user.city &&
      modalForm.address === user.address
    )
      return true;
    return false;
  };

  // ---------------- SAVE PROFILE ----------------
  const handleSaveProfile = async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/api/auth/update-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(modalForm),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || "Update failed", { duration: 2000 });
        return;
      }

      setUser(data.user);
      setFormData(data.user);
      setEditModalOpen(false);
      toast.success("Information updated!", { duration: 2000 });
      window.dispatchEvent(new Event("userUpdated"));
    } catch (err) {
      console.error(err);
      toast.error("Server error", { duration: 2000 });
    }
  };

  // ---------------- OTP HANDLERS ----------------
  const handleSendOtp = async () => {
    if (!modalForm.email || emailError || sendingOtp) return;

    setSendingOtp(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: modalForm.email }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpSent(true);
        setResendTimer(30);
        toast.success("OTP sent! Check your email.", { duration: 2000 });
      } else {
        toast.error(data.message || "Error sending OTP", { duration: 2000 });
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending OTP", { duration: 2000 });
    } finally {
      setSendingOtp(false);
    }
  };
  // ---------- 30 s resend otp timer-------
  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  // ------------verify otp------------
  const handleVerifyOtp = async () => {
    if (!otpInput) return;
    setVerifyingOtp(true);
    setOtpError("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: modalForm.email, otp: otpInput }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("userInfo", JSON.stringify(data.user));
        window.dispatchEvent(new Event("userUpdated"));
        toast.success("Logged in.", { duration: 2000 });
      } else {
        setOtpError(data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      setOtpError("Error verifying OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleCloseOtpModal = () => {
    setOtpModalOpen(false);
    setOtpSent(false);
    setOtpInput("");
    setOtpError("");
    setResendTimer(30);
  };

  // ---------------- PLACE ORDER ----------------
  const handlePlaceOrderAPI = async () => {
    if (orderCancelled) return;
    if (isOrderSubmitting) return;

    setIsOrderSubmitting(true);

    const token = localStorage.getItem("token");

    if (!token) {
      setOtpModalOpen(true);
      setIsOrderSubmitting(false);
      return;
    }

    try {
      const resUser = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resUser.ok) {
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");

        setUser(null);
        setFormData({
          name: "",
          email: "",
          phone: "",
          city: "",
          address: "",
        });

        window.dispatchEvent(new Event("userLoggedOut"));

        toast.error(
          "Your account no longer exists. Please login or register.",
          { duration: 3000 },
        );

        setIsOrderSubmitting(false);
        return;
      }

      const dataUser = await resUser.json();
      setUser(dataUser.user);
      setFormData(dataUser.user);
    } catch (err) {
      console.error(err);

      toast.error("Failed to verify user.", { duration: 3000 });

      setIsOrderSubmitting(false);
      return;
    }

    if (!cartItems.length) {
      toast.error("Your cart is empty.", { duration: 2000 });
      setIsOrderSubmitting(false);
      return;
    }

    const orderData = {
      customer: formData,
      cartItems: cartItems.map((item) => ({
        productId: item._id,
        name: item.name,
        size: item.size,
        quantity: item.quantity,
        price: Math.round(item.unitPrice),
        image: item.image || "",
      })),
      subtotal: Math.round(subtotal),
      tax: Math.round(tax),
      delivery: Math.round(delivery),
      totalAmount: Math.round(grandTotal),
      paymentMethod: "COD",
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/place-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      const data = await res.json();

      if (data.success) {
        setPlacedOrderId(data.orderId);
        setOrderPlaced(true);

        setCartItems([]);
        localStorage.removeItem("cart");
        sessionStorage.removeItem("buyNowItem");

        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        toast.error("Order failed: " + data.message, { duration: 2000 });
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.", { duration: 2000 });
    } finally {
      setIsOrderSubmitting(false);
      setProcessingOrder(false);
    }
  };

  // ---------------- ORDER PROGRESS ----------------
  useEffect(() => {
    if (!processingOrder) return;

    setOrderCancelled(false);
    setShowCancel(true);
    setProgress(0);

    const hideCancelTimer = setTimeout(() => {
      setShowCancel(false);
    }, 5000);

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }

        return prev + 2;
      });
    }, 100);

    // schedule API call
    if (!isOrderSubmitting) {
      orderTimeoutRef.current = setTimeout(() => {
        handlePlaceOrderAPI();
      }, 5000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(hideCancelTimer);

      if (orderTimeoutRef.current) {
        clearTimeout(orderTimeoutRef.current);
      }
    };
  }, [processingOrder]);

  // ---------------- CANCEL ORDER ----------------
  const cancelOrder = () => {
    if (orderTimeoutRef.current) {
      clearTimeout(orderTimeoutRef.current);
    }

    setOrderCancelled(true);
    setProcessingOrder(false);
    setProgress(0);

    toast.error("Order Cancelled!", { duration: 2000 });
  };
  // ---------------- JSX ----------------

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 pt-28 px-4 sm:px-6 lg:px-20 font-normal pb-10"
    >
      <Toaster position="bottom-right" />

      <motion.div
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: {
            transition: { staggerChildren: 0.15 },
          },
        }}
        className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12"
      >
        {/* LEFT */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 30 },
            show: { opacity: 1, y: 0 },
          }}
          className="lg:col-span-2 space-y-8"
        >
          <h1 className="text-2xl text-gray-900  xs:text-sm         font-normal lg:font-semibold">
            Checkout
          </h1>

          {/* Contact */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="bg-white rounded-2xl shadow-sm border p-6 space-y-6"
          >
            <div className="flex justify-between items-center">
              <h2 className="text-gray-800 text-sm font-semibold xs:text-xs">
                Contact Information
              </h2>

              {user && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setModalForm(formData);
                    setEditModalOpen(true);
                  }}
                  className="text-sm text-red-600 hover:underline xs:text-xs"
                >
                  Change
                </motion.button>
              )}
            </div>

            {/* Skeleton / Info / Login */}
            {step === "loading" ? (
              <div className="space-y-2">
                <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ) : user ? (
              <div className="text-sm text-gray-600 space-y-1 xs:text-xs">
                <p className="capitalize">
                  <span className="text-gray-500">name: </span> {formData.name}
                </p>

                <p>
                  <span className="text-gray-500">Email: </span>
                  {maskEmail(formData.email)}
                </p>

                <p>
                  <span className="text-gray-500">Phone: </span>
                  {maskMobile(formData.phone)}
                </p>

                <p className="capitalize">
                  <span className="text-gray-500">Address: </span>
                  {formData.address}, {formData.city}
                </p>
              </div>
            ) : (
              <div className="text-sm text-gray-600 space-y-2 xs:text-xs">
                <p>Please login to proceed with checkout.</p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setOtpModalOpen(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Login to Proceed
                </motion.button>
              </div>
            )}
          </motion.div>

          {/* Payment */}
          {step === "payment" && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border p-6 space-y-6"
            >
              <h2 className="text-gray-800 text-sm font-semibold xs:text-xs">
                Payment Method
              </h2>

              <div className="border rounded-xl p-5 bg-gradient-to-r from-red-50 to-white shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-normal xs:text-xs">
                    Cash on Delivery
                  </p>

                  <p className="text-xs text-gray-600 xs:text-[10px]">
                    Pay in cash when delivered.
                  </p>
                </div>

                <div className="bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                  Available
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setProcessingOrder(true)}
                disabled={cartItems.length === 0}
                className={`w-full py-3 mt-2 text-white rounded-lg ${
                  cartItems.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                Complete Order
              </motion.button>
            </motion.div>
          )}
        </motion.div>

        {/* RIGHT - Order Summary */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border p-4 w-full space-y-6 h-fit"
        >
          <h2 className="text-gray-800 text-base font-semibold ">
            Order Summary
          </h2>

          <div className="space-y-2 lg:space-y-4">
            {cartItems.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                className="flex justify-between items-center gap-3 text-sm xs:text-xs"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 rounded-lg object-contain shrink-0"
                    />
                  )}

                  <div className="flex items-center gap-1 min-w-0">
                    {/* Truncated part */}
                    <span className="truncate">
                      {item.quantity} × {item.name}
                    </span>

                    {/* Size (never truncated) */}
                    {item.size && (
                      <span className="uppercase text-gray-500 text-xs shrink-0">
                        ({item.size})
                      </span>
                    )}
                  </div>
                </div>

                <span className="font-medium shrink-0">
                  Rs {(item.unitPrice * item.quantity).toLocaleString("en-PK")}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="border-t pt-4 space-y-2 text-sm xs:text-xs font-normal">
            <div className="flex justify-between">
              <span>Total Items</span>
              <span>{totalItems}</span>
            </div>

            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>Rs {subtotal.toLocaleString("en-PK")}</span>
            </div>

            <div className="flex justify-between">
              <span>Tax (16%)</span>
              <span>Rs {tax.toLocaleString("en-PK")}</span>
            </div>

            <div className="flex justify-between">
              <span>Delivery</span>
              <span>Rs {delivery.toLocaleString("en-PK")}</span>
            </div>

            <div className="flex justify-between border-t pt-3 text-base font-medium">
              <span>Grand Total</span>
              <span>Rs {grandTotal.toLocaleString("en-PK")}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* ---------------- Modals ---------------- */}

      {/* ---------------Edit modal ---------------- */}
      <AnimatePresence>
        {editModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm z-50 px-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-7 space-y-5"
            >
              {/* Close button */}
              <button
                onClick={() => setEditModalOpen(false)}
                className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl"
              >
                &times;
              </button>

              <fieldset className="relative border border-gray-200 rounded-xl p-5 pt-6">
                <legend className="absolute -top-2 left-3 bg-white px-2 text-xs tracking-wide uppercase text-gray-500">
                  Delivery Information
                </legend>

                <div className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    value={modalForm.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />

                  <input
                    type="tel"
                    name="phone"
                    value={modalForm.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />

                  {phoneError && (
                    <p className="text-red-600 text-xs">{phoneError}</p>
                  )}

                  <input
                    type="text"
                    name="city"
                    value={modalForm.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />

                  <textarea
                    name="address"
                    value={modalForm.address}
                    onChange={handleChange}
                    placeholder="Delivery Address"
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>
              </fieldset>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaveDisabled() || isSaving}
                  className={`px-5 py-2 rounded-lg text-white flex items-center justify-center gap-2 transition ${
                    isSaveDisabled() || isSaving
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {isSaving ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ------------------otp modal------------- */}

      <OtpModal
        otpModalOpen={otpModalOpen}
        setOtpModalOpen={handleCloseOtpModal}
        user={user}
        otpSent={otpSent}
        setOtpSent={setOtpSent}
        formData={modalForm}
        setFormData={setModalForm}
        handleSendOtp={handleSendOtp}
        otpInput={otpInput}
        setOtpInput={setOtpInput}
        otpError={otpError}
        setOtpError={setOtpError}
        verifyingOtp={verifyingOtp}
        handleVerifyOtp={handleVerifyOtp}
        resendTimer={resendTimer}
        setResendTimer={setResendTimer}
        emailError={emailError}
        sendingOtp={sendingOtp}
      />

      {processingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 flex flex-col items-center text-center space-y-6">
            {/* Title */}
            <h2 className="text-lg sm:text-xl font-medium text-gray-900 flex items-center gap-2">
              {!showCancel ? (
                <>
                  Preparing your order
                  <span className="flex space-x-1">
                    <span className="w-2 h-2 bg-red-600 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-red-600 rounded-full animate-bounce [animation-delay:150ms]"></span>
                    <span className="w-2 h-2 bg-red-600 rounded-full animate-bounce [animation-delay:300ms]"></span>
                  </span>
                </>
              ) : (
                "Processing your order"
              )}
            </h2>

            {/* Progress + Cancel */}
            {showCancel && (
              <div className="w-full space-y-4">
                {/* Progress bar */}
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-600 transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {/* Cancel */}
                <button
                  onClick={cancelOrder}
                  className="w-full sm:w-auto px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel Order
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {orderPlaced && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 flex flex-col items-center text-center space-y-6">
            {/* Close */}
            <button
              onClick={() => {
                setOrderPlaced(false);
                setProcessingOrder(false);
              }}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-xl"
            >
              &times;
            </button>

            {/* Success Icon */}
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-7 h-7 text-green-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-medium text-gray-900">
              Order Placed Successfully
            </h2>

            {/* Order ID */}
            <p className="text-gray-600 text-sm sm:text-base">
              Your Order ID
              <span className="ml-2 font-medium text-gray-900">
                #{placedOrderId}
              </span>
            </p>

            {/* Button */}
            <Link
              to="/menu"
              onClick={() => setOrderPlaced(false)}
              className="w-full"
            >
              <button className="w-full py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition">
                Back to Menu
              </button>
            </Link>
          </div>
        </div>
      )}
    </motion.div>
  );
}
